import React, { type FC } from 'react';
import styled from 'styled-components';
import ReactPlayer from 'react-player/lazy';
import ReactSwiper from '../react-swiper';
import { useFancyBox } from '../../hooks/useFancyBox';
import LazyImage from '../lazy-image';
import { getFluidGatsbyImage } from '../../utils/gatsby-storyblok-image-fixed';
import { Flex, Grid } from '../UIKit/system';
import { VideoPlayer } from '../video-player';
import ArrowRight from '../../images/svg/arrow-right.svg';
import { StoryblokProductComponentProps } from '../storyblok/storyblok-product-component';
import IconButton from '../icon-button';
import { getThemeColor, hoverScaleDefault } from '../../utils/styles';

interface ProductGalleryProps {
  items: StoryblokProductComponentProps['gallery'];
}

export const ProductGallery: FC<ProductGalleryProps> = (props) => {
  const { items } = props;
  const [fancyBoxRef] = useFancyBox();
  const videoItems = items?.filter((item) => !!item.videoUrl || !!item.video);
  const swiperOptions = {
    loop: false,
    centeredSlides: true,
    slidesPerView: 1,
    navigation: {
      prevEl: '.productGalleryPrevious',
      nextEl: '.productGalleryNext'
    },
    pagination: {
      el: '.swiper-pagination',
      clickable: true
    }
  };

  return (
    <Container style={{ borderRadius: 'var(--Radius-Radius-3, 16px)' }}>
      <SwiperContainer>
        <ReactSwiper options={swiperOptions} ref={fancyBoxRef}>
          {items?.map((item) => {
            const image = getFluidGatsbyImage(item.image);

            return (
              <ReactSwiper.Item
                key={item._uid}
                data-fancybox="gallery"
                data-src={
                  item.videoUrl || item.video ? `#${item._uid}` : image.src
                }
                data-caption={item.title}
                style={{ borderRadius: 'var(--Radius-Radius-3, 16px)' }}
              >
                <LazyImage fluid={image} alt={item.title} />
              </ReactSwiper.Item>
            );
          })}
        </ReactSwiper>
        <ArrowButton className="productGalleryPrevious" />
        <ArrowButton className="productGalleryNext" />
      </SwiperContainer>
      <NavContainer>
        <StyledPagination className="swiper-pagination" />
        <ZoomButton
          onClick={() => {
            // Workaround for opening the gallery on the current slide
            const activeSlide = document.getElementsByClassName(
              'swiper-slide-active'
            )[0] as HTMLElement | undefined;
            activeSlide?.click();
          }}
        />
      </NavContainer>
      {videoItems?.map((item) => (
        <VideoContent key={item._uid} id={item._uid.toString()}>
          {item.videoUrl ? (
            <ReactPlayer url={item.videoUrl} />
          ) : (
            item.video?.filename && (
              <VideoPlayer
                videoSource={item.video?.filename}
                thumbnailSource={item.image}
                autoPlay={false}
              />
            )
          )}
        </VideoContent>
      ))}
    </Container>
  );
};

const Container = styled(Flex).attrs(() => ({
  backgroundColor: 'lightGrey',
  flexDirection: 'column',
  overflow: 'hidden'
}))``;

const SwiperContainer = styled(Flex).attrs(() => ({
  mt: [20, 20, 40],
  mx: [15, 30],
  overflow: 'hidden',
  position: 'relative'
}))``;

const NavContainer = styled(Grid).attrs(() => ({
  gridAutoFlow: 'column',
  gridTemplateColumns: 'auto 1fr auto',
  mx: [15, 30]
}))`
  &:before {
    content: '';
    width: 48px;
  }
`;

/**
 * Apply custom styling to Swiper pagination.
 */
const StyledPagination = styled.div`
  align-content: center;
  display: grid;
  grid-auto-flow: column;
  gap: 10px;
  justify-content: center;
  padding-top: 22px;
  padding-bottom: 43px;
  z-index: 1;

  .swiper-pagination-bullet {
    display: inline-grid;
    background-color: ${getThemeColor('grey4')};
    border-radius: 50%;
    height: 8px;
    width: 8px;
    cursor: pointer;

    &-active {
      background-color: ${getThemeColor('grey')};
    }
  }
`;

const VideoContent = styled.div`
  display: none;
`;

const ZoomButton = styled(IconButton).attrs(() => ({
  icon: { name: 'zoom' },
  size: 50,
  justifySelf: 'end'
}))``;

const StyledArrowRight = styled(ArrowRight)`
  stroke: ${getThemeColor('black')};
`;

const ArrowButton = styled.button.attrs(() => ({
  children: <StyledArrowRight />
}))`
  background-color: ${getThemeColor('white')};
  border: none;
  border-radius: 50%;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  height: 48px;
  opacity: 0;
  position: absolute;
  top: calc(50% - 24px);
  width: 48px;
  visibility: hidden;

  &[aria-disabled='false'] {
    ${hoverScaleDefault}
  }

  ${SwiperContainer}:hover &[aria-disabled="false"] {
    opacity: 1;
    visibility: visible;
  }

  ${SwiperContainer}:hover &[aria-disabled="true"] {
    cursor: not-allowed;
    opacity: 0.6;
    visibility: visible;
  }

  &.productGalleryPrevious {
    left: 10px;

    svg {
      transform: scaleX(-1);
    }
  }

  &.productGalleryNext {
    right: 10px;
  }
`;
